/**
 * The agent file surface (`read`, `write`, `edit`), shared by every
 * playground-backed host (Bear's apprentices, Hearth's chat agents).
 *
 * SUP-278: the transcript is a replay log. No tool here ever rewrites an
 * earlier tool result (that mutates the cached prompt prefix and rebuilds
 * it); staleness is answered additively, with markers and catch-up patches.
 * Results carry the doc heads they were produced at, and {@link seenState}
 * derives what the model holds from the message history, so there is no
 * side-band ledger to evict: a blanked result loses its stamp with its bytes.
 *
 * Every host is versioned (`./file-host`, or a bridge to it): results carry
 * heads, `write` is gated on them, and `edit` splices atomically. No lock,
 * no serialization.
 */
import { tool, type ModelMessage, type Tool } from "ai";
import { z } from "zod";

/** The AI SDK's projected tool result: what `toModelOutput` returns. */
export type ToolResultOutput = Awaited<
  ReturnType<NonNullable<Tool["toModelOutput"]>>
>;

/** Doc heads: the content-addressed name of a versioned file's exact state. */
export type FileHeads = string[];

/**
 * A box on the page, as fractions of it, origin at the top-left corner:
 * `{x: 0.5, y: 0, width: 0.5, height: 0.5}` is the top-right quarter. The
 * frame is the page rather than pixels or points, so the same box means the
 * same thing on any sheet and at any render size.
 */
export interface PageRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The slice a `read` asks for: 0-indexed lines (pages for a PDF host). */
export interface ReadWindow {
  offset?: number;
  range?: number;
  region?: PageRegion;
}

export type FileReadResult =
  | { ok: true; path: string; content: string; heads?: FileHeads }
  // Files to look at, as stored references rather than bytes: a
  // `ModelFile[]` (`./model-images`), named structurally so the file tools
  // don't depend on the module that resolves them.
  | {
      ok: true;
      path: string;
      files: { url: string; mediaType: string }[];
      content?: string;
    }
  | { ok: false; error: string };

export type FileWriteResult =
  | {
      ok: true;
      path: string;
      action: "created" | "updated";
      /** Heads after the write. */
      heads: FileHeads;
      /** Landed, but needs the agent's attention (e.g. a bad App manifest). */
      warning?: string;
    }
  | { ok: false; error: string };

/** What the model last saw for a path, handed to a versioned `write`'s gate. */
export interface WriteGate {
  /** Heads of the state the model holds IN FULL; null = nothing, or only a
   *  slice (whose unseen remainder an overwrite would destroy). */
  seenHeads: FileHeads | null;
}

export interface EditInput {
  path: string;
  old_string: string;
  new_string?: string;
  replace_all?: boolean;
}

/** What the model missed between its last-seen state and the pre-edit doc. */
export type EditGap =
  | { kind: "all"; lines: number }
  | { kind: "none" }
  | { kind: "patch"; patch: string; changed: number }
  | { kind: "large"; changed: number | null };

/** A successful atomic edit: the post-edit heads plus the model's gap. */
export interface FileEditOutcome {
  ok: true;
  path: string;
  action: "updated";
  heads: FileHeads;
  gap: EditGap;
  warning?: string;
}

/** The patch from a seen state to the doc's current state. */
export interface FileDiffSince {
  heads: FileHeads;
  patch: string;
  changed: number;
}

/** Host operations. `read` owns the window. */
export interface FileToolHost {
  read(path: string, window: ReadWindow): Promise<FileReadResult>;
  /** Create, or overwrite when `gate` holds the doc's current heads. */
  write(
    path: string,
    content: string,
    gate?: WriteGate,
  ): Promise<FileWriteResult>;
  /** Match and splice atomically: no gap between match and mutation. */
  editAtomic(
    input: EditInput,
    seenHeads: FileHeads | null,
  ): Promise<FileEditOutcome | { ok: false; error: string }>;
  /** The patch from `from` to now; null when it can't be computed. */
  diff(path: string, from: FileHeads): Promise<FileDiffSince | null>;
}

/** A file's text narrowed to `range` lines from 0-indexed `offset`. */
export function sliceLines(
  text: string,
  { offset, range }: ReadWindow,
): string {
  if (offset === undefined && range === undefined) return text;
  const lines = text.split("\n");
  const start = offset ?? 0;
  const end = range === undefined ? undefined : start + range;
  return lines.slice(start, end).join("\n");
}

/** A read returning more than this refuses and names a window instead. */
const MAX_WHOLE_READ_CHARS = 256 * 1024;

// ---------------------------------------------------------------------------
// The derived ledger: what the model has seen, read off the transcript.
// ---------------------------------------------------------------------------

const FILE_TOOL_NAMES = new Set(["read", "write", "edit"]);

/** The doc state the model last saw, and which slice it still holds bytes for. */
export interface SeenState {
  seenHeads: FileHeads | null;
  shown: ReadWindow | "full" | null;
}

/** `value.heads` as heads, explicit null, or undefined when absent/malformed. */
function asHeads(value: unknown): FileHeads | null | undefined {
  if (value === null) return null;
  return Array.isArray(value) && value.every((h) => typeof h === "string")
    ? value
    : undefined;
}

/** The ledger-relevant fields of a stored file-tool result. */
interface StampedValue {
  ok?: unknown;
  path?: unknown;
  heads?: unknown;
  content?: unknown;
  offset?: unknown;
  range?: unknown;
}

/**
 * Newest heads stamp decides `seenHeads`; newest content-bearing result (a
 * read's text, or a write, whose params were the whole file) decides `shown`.
 * Tombstones and catch-ups carry heads but no bytes.
 */
export function seenState(messages: ModelMessage[], path: string): SeenState {
  let seenHeads: FileHeads | null | undefined;
  let shown: ReadWindow | "full" | null | undefined;
  outer: for (let mi = messages.length - 1; mi >= 0; mi--) {
    const msg = messages[mi]!;
    if (msg.role !== "tool") continue;
    for (let pi = msg.content.length - 1; pi >= 0; pi--) {
      const part = msg.content[pi]!;
      if (
        part.type !== "tool-result" ||
        !FILE_TOOL_NAMES.has(part.toolName) ||
        part.output.type !== "json"
      ) {
        continue;
      }
      const value = part.output.value as StampedValue | null;
      if (!value || value.ok !== true || value.path !== path) continue;
      if (seenHeads === undefined && "heads" in value) {
        const heads = asHeads(value.heads);
        if (heads !== undefined) seenHeads = heads;
      }
      if (shown === undefined) {
        if (part.toolName === "write") shown = "full";
        else if (
          part.toolName === "read" &&
          typeof value.content === "string"
        ) {
          shown =
            value.offset === undefined && value.range === undefined
              ? "full"
              : {
                  offset:
                    typeof value.offset === "number" ? value.offset : undefined,
                  range:
                    typeof value.range === "number" ? value.range : undefined,
                };
        }
      }
      if (seenHeads !== undefined && shown !== undefined) break outer;
    }
  }
  return { seenHeads: seenHeads ?? null, shown: shown ?? null };
}

/** Heads equality, order-insensitive. */
export function sameHeads(a: FileHeads, b: FileHeads): boolean {
  return a.length === b.length && [...a].sort().join() === [...b].sort().join();
}

/** Whether the bytes the model holds (`shown`) include `window`. */
function covers(shown: ReadWindow | "full", window: ReadWindow): boolean {
  if (shown === "full") return true;
  if (window.offset === undefined && window.range === undefined) return false;
  const shownStart = shown.offset ?? 0;
  const shownEnd =
    shown.range === undefined ? Infinity : shownStart + shown.range;
  const askStart = window.offset ?? 0;
  const askEnd =
    window.range === undefined ? Infinity : askStart + window.range;
  return askStart >= shownStart && askEnd <= shownEnd;
}

/** Which file a successful `read` covered, and at what window (media too). */
export function readResultId(
  value: unknown,
): { path: string; window: ReadWindow } | null {
  const read = value as StampedValue | null;
  if (!read || read.ok !== true || typeof read.path !== "string") return null;
  return {
    path: read.path,
    window: {
      offset: typeof read.offset === "number" ? read.offset : undefined,
      range: typeof read.range === "number" ? read.range : undefined,
    },
  };
}

// ---------------------------------------------------------------------------
// Tool descriptors
// ---------------------------------------------------------------------------

/** One `read` on every host: a window is lines of text or pages of a PDF,
 *  by the file's type. The ledger note is a complete sentence appended. */
export const readToolDescriptor = {
  description:
    "Read a file by path. Text comes back as text; an image comes back as itself and a PDF page as a picture of it, which you can look at directly in the result. Pass `offset` and `range` to read part of a file: a 0-indexed start line and a line count for text, a start page and a page count for a PDF. Omit `region` on ordinary reads. After reading a PDF page whole, pass `region` only to zoom into a specific part whose detail you need. Errors if the file does not exist or is a type you can't read (an archive, a font)." +
    " If you already hold the file's current contents, the result is a short [unchanged] marker instead of the text. If the file changed since you read it, the result may be a [catch-up] patch of just the changes; apply it to what you have instead of asking for the file again.",
  inputSchema: z.object({
    path: z.string().describe("Path to the file to read."),
    offset: z
      .number()
      .optional()
      .describe("0-indexed start: line for text, page for a PDF."),
    range: z
      .number()
      .optional()
      .describe(
        "How much to read from `offset`: lines for text, pages for a PDF.",
      ),
    region: z
      .object({
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
        width: z.number().min(0).max(1),
        height: z.number().min(0).max(1),
      })
      .optional()
      .describe(
        "Optional crop of one PDF page, in fractions of its width and height from the top-left corner. Omit this field for a whole-page read; do not pass a full-page box. Use it only after seeing the whole page and choosing a specific area to inspect. When present, pass the page index in `offset` and omit `range`.",
      ),
  }),
};

export const writeToolDescriptor = {
  description:
    "Create a new file or completely overwrite an existing one with the given content. Use this for new files, or when you're rewriting most of a file. To change a specific piece of an existing file, use `edit` instead. Overwriting an existing file requires that you have read it in full this conversation (not just a slice) and that it hasn't changed since your read; otherwise the write is rejected, and the rejection shows what you're missing.",
  inputSchema: z.object({
    path: z.string().describe("Path to the file to create or overwrite."),
    content: z.string().describe("The complete contents of the file."),
  }),
};

export const editToolDescriptor = {
  description:
    "Replace a specific piece of text in an existing file. Pass `old_string` (the exact text to find) and `new_string` (the replacement). `old_string` must match the file's current contents exactly and uniquely identify one location, so include enough surrounding context, or set `replace_all` to replace every occurrence. Omit `new_string` to delete the matched text. The match runs against the file as it is on disk right now; if the file changed since you last saw it, the result tells you what you missed. To create a file or overwrite it entirely, use `write`.",
  inputSchema: z.object({
    path: z.string().describe("Path to the file to edit."),
    old_string: z
      .string()
      .describe(
        "The exact existing text to replace. Must uniquely identify one location unless `replace_all` is set.",
      ),
    new_string: z
      .string()
      .optional()
      .describe(
        "The replacement for `old_string`. Defaults to empty (deletes the matched text).",
      ),
    replace_all: z
      .boolean()
      .optional()
      .describe(
        "Replace every occurrence of `old_string` instead of requiring a unique match. Defaults to false.",
      ),
  }),
};

// ---------------------------------------------------------------------------
// Result composition
// ---------------------------------------------------------------------------

const kb = (chars: number): string => (chars / 1024).toFixed(1);

const CONTEXT_CURRENT_NOTE =
  "(file state is current in your context; no need to read it back)";

/** The atomic host edit, reporting the model's gap. */
async function runEdit(
  host: FileToolHost,
  input: EditInput,
  messages: ModelMessage[],
) {
  const seen = seenState(messages, input.path);
  const result = await host.editAtomic(input, seen.seenHeads);
  if (!result.ok) return result;
  const report = {
    ok: true as const,
    path: result.path,
    action: result.action,
    ...(result.warning ? { warning: result.warning } : {}),
  };
  const gap = result.gap;
  switch (gap.kind) {
    case "none":
      return {
        ...report,
        heads: result.heads,
        note: `The edit is applied. ${CONTEXT_CURRENT_NOTE}`,
      };
    case "patch":
      return {
        ...report,
        heads: result.heads,
        patch: gap.patch,
        note: `The edit is applied. The file also has ${gap.changed} changed line(s) you had not seen, shown in \`patch\`; with those, your context is current.`,
      };
    case "large":
      // Most of the file is unseen: drop the stamp so the next read is cold.
      return {
        ...report,
        heads: null,
        note: `The edit is applied, but the file has ${gap.changed ?? "many"} changed line(s) you have not seen. Read ${input.path} before relying on its contents.`,
      };
    case "all":
      return {
        ...report,
        note: `The edit is applied. (you have not read this file; it is ${gap.lines} lines total)`,
      };
  }
}

/**
 * Build the `read` / `write` / `edit` tool set over one host.
 *
 * `readToModelOutput` projects a read result for the model at every prompt
 * build, so anything expiring (a signed URL) is minted there, not stored in
 * the result. It takes `unknown`: a host's read result is a superset of
 * {@link FileReadResult} that only the host knows how to narrow.
 */
export function makeFileTools(
  host: FileToolHost,
  readToModelOutput: (
    output: unknown,
  ) => ToolResultOutput | Promise<ToolResultOutput>,
) {
  return {
    read: tool({
      description: readToolDescriptor.description,
      inputSchema: readToolDescriptor.inputSchema,
      strict: false,
      toModelOutput: ({ output }) => readToModelOutput(output),
      // The window rides on the result so the derived ledger knows what the
      // model actually saw.
      execute: async ({ path, offset, range, region }, { messages }) => {
        // A box is on one page, so the ask names it and covers nothing else:
        // without `offset` the host would read the PDF whole and the crop
        // would quietly not happen, and a page range would crop every page.
        if (region && (offset === undefined || (range ?? 1) !== 1)) {
          return {
            ok: false as const,
            error: `\`region\` looks closer at one page of ${path}: pass \`offset\` (the 0-indexed page) with it and no \`range\`.`,
          };
        }
        const window = {
          offset,
          range: region ? 1 : range,
          ...(region ? { region } : {}),
        };
        const result = await host.read(path, window);
        // A read that came back as files is done: the window it honored is
        // the pages it rendered, and there is no text to gate on size.
        if (!result.ok || "files" in result || !("content" in result)) {
          return result;
        }
        // Gate returned size: `offset` with no `range` is still the whole tail.
        if (result.content.length > MAX_WHOLE_READ_CHARS) {
          return {
            ok: false as const,
            error: `That read of ${path} returns ${kb(result.content.length)} KB, too much at once. Pass \`offset\` and a bounded \`range\` to read it in slices (e.g. offset: 0, range: 500).`,
          };
        }
        // Stamped with the window the host was given, which is what the
        // derived ledger reads back as what the model saw.
        const full = { ...result, offset: window.offset, range: window.range };
        if (!result.heads) return full;
        const seen = seenState(messages, path);
        if (seen.seenHeads && sameHeads(seen.seenHeads, result.heads)) {
          if (seen.shown && covers(seen.shown, window)) {
            return {
              ok: true as const,
              path,
              unchanged: true as const,
              heads: result.heads,
              note: `[unchanged] ${path} is at the version you already hold from your earlier read. ${result.content.split("\n").length} lines, ${kb(result.content.length)} KB not re-sent.`,
            };
          }
          return full;
        }
        // Behind. A catch-up patch only makes sense against a full base.
        if (seen.seenHeads && seen.shown === "full") {
          const gap = await host.diff(path, seen.seenHeads);
          if (
            gap &&
            sameHeads(gap.heads, result.heads) &&
            gap.patch.length < result.content.length / 2
          ) {
            return {
              ok: true as const,
              path,
              catchUp: true as const,
              heads: result.heads,
              patch: gap.patch,
              note: `[catch-up] ${path} advanced by ${gap.changed} changed line(s) since you last read it. The \`patch\` brings you current; nothing else differs.`,
            };
          }
        }
        return full;
      },
    }),

    write: tool({
      description: writeToolDescriptor.description,
      inputSchema: writeToolDescriptor.inputSchema,
      execute: async ({ path, content }, { messages }) => {
        const seen = seenState(messages, path);
        // A heads-current slice doesn't license an overwrite: gate on a
        // fully-held state only.
        const result = await host.write(path, content, {
          seenHeads: seen.shown === "full" ? seen.seenHeads : null,
        });
        if (!result.ok) return result;
        return { ...result, note: CONTEXT_CURRENT_NOTE };
      },
    }),

    edit: tool({
      description: editToolDescriptor.description,
      inputSchema: editToolDescriptor.inputSchema,
      execute: (input: EditInput, { messages }) => {
        if (input.old_string === (input.new_string ?? "")) {
          return Promise.resolve({
            ok: false as const,
            error: `old_string and new_string are identical: nothing to change in ${input.path}.`,
          });
        }
        return runEdit(host, input, messages);
      },
    }),
  };
}
